import { useEffect, useState } from "react";

import getStripe, { retryLoadStripe } from "@/utils/getStripe";

// How often to re-check while blocked. A proxy toggle happens at the OS or
// browser level with no event we can listen for, so we poll — cheaply, since a
// blocked script fails fast — and also retry on focus/online, which cover the
// "came back to the tab after disconnecting" case immediately.
const RETRY_INTERVAL_MS = 30_000;

/**
 * Whether Stripe.js failed to load — most often because the browser is
 * connected through a Webshare proxy, which blocks js.stripe.com (r-1956).
 *
 * Starts `false` so nothing flashes in the common case, flips to `true` once
 * the load has definitively failed (after getStripe's single recovery reload),
 * and back to `false` as soon as a retry succeeds, i.e. the proxy is gone.
 */
export default function useStripeLoadFailed(): boolean {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    // A null result on the client means js.stripe.com never loaded — see
    // getStripe, which resolves null once its one recovery reload is spent.
    void getStripe().then((stripe) => {
      if (active) setFailed(!stripe);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!failed) return;
    let active = true;
    const retry = () => {
      if (document.visibilityState === "hidden") return;
      void retryLoadStripe().then((stripe) => {
        if (active && stripe) setFailed(false);
      });
    };
    const timer = window.setInterval(retry, RETRY_INTERVAL_MS);
    window.addEventListener("focus", retry);
    window.addEventListener("online", retry);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", retry);
      window.removeEventListener("online", retry);
    };
  }, [failed]);

  return failed;
}
